import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { LayoutDashboard, FileText, PlusCircle, BarChart3, Settings, Shield, LogOut, Ship, Users, Menu, X, UserCheck, Globe } from 'lucide-react';
import NotificationPanel from './NotificationPanel';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.role;
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Build nav items based on role
  const navItems = [];

  // Dashboard — all roles see it (data is filtered per role on backend)
  navItems.push({ path: '/', label: t('dashboard'), icon: LayoutDashboard, testId: 'nav-dashboard' });

  // Offers — all roles
  navItems.push({ path: '/offers', label: t('offers'), icon: FileText, testId: 'nav-offers', exact: true });

  // My Offers — all roles
  navItems.push({ path: '/my-offers', label: t('my_offers'), icon: UserCheck, testId: 'nav-my-offers' });

  // Create Offer — all roles
  navItems.push({ path: '/offers/new', label: t('create_offer'), icon: PlusCircle, testId: 'nav-create-offer' });

  // Analytics — ADMIN and SUPER_ADMIN only
  if (role === 'ADMIN' || role === 'SUPER_ADMIN') {
    navItems.push({ path: '/analytics', label: t('analytics'), icon: BarChart3, testId: 'nav-analytics' });
  }

  // Settings — ADMIN and SUPER_ADMIN
  if (role === 'ADMIN' || role === 'SUPER_ADMIN') {
    navItems.push({ path: '/settings', label: t('settings'), icon: Settings, testId: 'nav-settings' });
  }

  // Platform Admin — SUPER_ADMIN only
  if (role === 'SUPER_ADMIN') {
    navItems.push({ path: '/platform-admin', label: 'Platform Admin', icon: Shield, testId: 'nav-platform-admin' });
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 text-white font-dm flex flex-col flex-shrink-0 transform transition-transform duration-200 md:static md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ship size={28} className="text-orange-500" />
            <div>
              <h1 className="text-xl font-medium">Routemind</h1>
              <p className="text-xs text-slate-400 capitalize">{role?.toLowerCase()}</p>
            </div>
          </div>
          <button
            type="button"
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = item.path === '/' ? location.pathname === '/' : item.exact ? location.pathname === item.path : isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-rm-teal-500 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                data-testid={item.testId}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 px-4 py-2 mb-3">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
              <Users size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleLanguage}
            className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors mb-1"
            data-testid="lang-toggle"
          >
            <Globe size={18} />
            {language === 'EN' ? 'Türkçe' : 'English'}
          </button>
          <Button
            variant="ghost"
            className="w-full text-slate-400 hover:text-white hover:bg-slate-800 justify-start"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut size={18} className="mr-3" />
            {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 flex-shrink-0">
          <button
            type="button"
            className="md:hidden text-slate-600 hover:text-slate-900"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={22} />
          </button>
          <div className="ml-auto">
            <NotificationPanel />
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
